import * as Sentry from "@sentry/react";

Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN || "",

    environment: process.env.NODE_ENV || "development",

    sendDefaultPii: true,

    tracesSampleRate: process.env.NODE_ENV === "production" ? 0.1 : 1.0,

    debug: process.env.NODE_ENV === "development",

    beforeSend(event, hint) {
        // Filter out errors from browser extensions and third-party scripts
        const error = hint.originalException || hint.syntheticException;

        // Check if error is from a browser extension
        if (event.exception?.values?.[0]?.stacktrace?.frames) {
            const frames = event.exception.values[0].stacktrace.frames;
            const hasExtensionFrame = frames.some(frame =>
                frame.filename?.includes('chrome-extension://') ||
                frame.filename?.includes('moz-extension://') ||
                frame.filename?.includes('safari-extension://') ||
                frame.filename?.includes('edge-extension://')
            );

            if (hasExtensionFrame) {
                return null; // Don't send to Sentry
            }
        }

        // Check for IAB bridge errors
        if (error?.message?.includes('__iabjs_unified_bridge') ||
            error?.message?.includes('UserMessageHandlersNamespace')) {
            return null;
        }

        // Check for Java bridge errors
        if (error?.message?.includes('Java object is gone') ||
            error?.message?.includes('enableDidUserTypeOnKeyboardLogging')) {
            return null;
        }

        // Check for iOS WebKit messageHandlers errors
        if (error?.message?.includes('webkit.messageHandlers') ||
            error?.message?.includes('window.webkit.messageHandlers') ||
            error?.message?.includes('messageHandlers[e].postMessage')) {
            return null;
        }

        // Check stack trace for webkit.messageHandlers errors
        if (event.exception?.values?.[0]?.stacktrace?.frames) {
            const frames = event.exception.values[0].stacktrace.frames;
            const hasWebKitError = frames.some(frame =>
                frame.function?.includes('webkit') ||
                frame.module === '<unknown module>' &&
                (frame.context_line?.includes('meta property') ||
                    frame.context_line?.includes('og:type'))
            );

            if (hasWebKitError && error?.message?.includes('undefined is not an object')) {
                return null;
            }
        }

        if (process.env.NODE_ENV === "development") {
            console.error("Sentry Error:", error);
        }

        return event;
    },

    ignoreErrors: [
        "top.GLOBALS",
        "originalCreateNotification",
        "canvas.contentDocument",
        "MyApp_RemoveAllHighlights",
        "http://tt.epicplay.com",
        "Can't find variable: ZiteReader",
        "jigsaw is not defined",
        "ComboSearch is not defined",
        "http://loading.retry.widdit.com/",
        "atomicFindClose",

        "NetworkError",
        "Network request failed",

        "AbortError",
        "Request aborted",

        // IAB (Interactive Advertising Bureau) bridge errors - third party ad scripts
        "__iabjs_unified_bridge_nonce",
        "s is not a function",
        /UserMessageHandlersNamespace/,

        // Java bridge errors
        "Java object is gone",
        "enableDidUserTypeOnKeyboardLogging",
        /Java object/i,

        // iOS WebKit messageHandlers errors
        "webkit.messageHandlers",
        "window.webkit.messageHandlers",
        /webkit\.messageHandlers/i,
        /undefined is not an object.*webkit/i,
        /undefined is not an object.*messageHandlers/i,
        /messageHandlers\[.*\]\.postMessage/i,

        // Other common third-party script errors
        "ResizeObserver loop limit exceeded",
        "ResizeObserver loop completed with undelivered notifications",
    ],

    beforeBreadcrumb(breadcrumb, hint) {
        if (breadcrumb.category === "console") {
            return null;
        }
        return breadcrumb;
    },

    integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
            maskAllText: true,
            blockAllMedia: true,
        }),
    ],

    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
});

export default Sentry;
